import { z } from "zod";
// The contract of customer memory transparency (CHT-722, design `docs/architecture/phase-5-memory-api.md`
// and `phase-5-memory.md` 10.2): the six `/customer/memory` routes, the customer-safe fact view and the
// shared memory codes. FROZEN at the stage 0 commit of lane J. Lanes K (admin) and L (mobile) build on it.
//
// Same rules as `domain-views.ts` and `staff-catalog.ts`. Every top-level shape is STRICT: a key that is not
// listed here fails the parse (the API answers 500 and nothing leaks; a client treats it as a contract
// error). Times are ISO 8601 text (`2026-10-15T09:30:00.000Z`), because the same schema parses the JSON in
// the API and in the typed client.
//
// What a customer sees of a fact: its id, kind, value, where it came from (`origin`), when it was last
// confirmed and its version. A customer NEVER sees confidence, source type, sensitivity, source reference,
// extraction run, policy version, review reason, status or an actor: no field for them exists below.
//
// This package cannot import the domain package. The tuples and the vocabulary below repeat the domain's
// (`customer-memory.ts`, `memory-taxonomy.ts`); `apps/api` has a parity test that fails when one differs.
// --- codes (parity-tested against the domain) -------------------------------------------------------
export const CUSTOMER_MEMORY_VIEW_VERSION = "customer-memory-view.v1";
/**
 * The title of the customer's memory screen in the mobile app. It is English in every language: the concierge prompt (rule 8) tells the
 * model to write this exact title when it points the customer to the screen, so the prompt and the app read it from here.
 */
export const MEMORY_SCREEN_TITLE = "What Lafayette remembers";
export const memoryKindValues = [
    "DIETARY_PREFERENCE",
    "DINING_STYLE",
    "PROPERTY_PREFERENCE",
    "SCHEDULE_PREFERENCE",
    "INTEREST",
    "DISLIKED_EXPERIENCE",
    "TRAVEL_COMPANION_PATTERN",
    "EXPLICIT_INSTRUCTION",
];
export const memoryStatusValues = [
    "CANDIDATE",
    "ACTIVE",
    "DISPUTED",
    "SUPERSEDED",
    "EXPIRED",
    "DELETED",
];
export const memoryProvenanceValues = [
    "CUSTOMER_CONFIRMED",
    "STAFF_CONFIRMED",
    "CUSTOMER_STATED_IN_CHAT",
    "MODEL_INFERRED",
];
/** The customer's wording of the provenance: "You told us", "You added", "Added by your concierge". */
export const customerMemoryOriginValues = [
    "YOU_TOLD_US",
    "YOU_ADDED",
    "ADDED_BY_YOUR_CONCIERGE",
];
/** `CODE`: a value of the closed vocabulary (show its label key). `TEXT`: the customer's own short text. */
export const memoryFormValues = ["CODE", "TEXT"];
/** The two kinds whose value may also be a short free text. Every other kind takes a code only. */
export const memoryTextKindValues = ["DISLIKED_EXPERIENCE", "EXPLICIT_INSTRUCTION"];
/** The bounds of a value a client may send: a code is at most 48 characters, a text 3 to 160. */
export const MEMORY_VALUE_LIMITS = Object.freeze({ textMin: 3, textMax: 160, codeMax: 48 });
/** The closed staff reason codes (`memoryStaffReasonCodes` of the domain). A reason is a code, never free text. */
export const memoryStaffReasonCodeValues = [
    "CUSTOMER_REQUESTED",
    "CUSTOMER_CONFIRMED_VERBALLY",
    "VERIFIED_FROM_TRIP",
    "INCORRECT_EXTRACTION",
    "OUTDATED",
    "DUPLICATE",
    "NOT_ALLOWED_SENSITIVE",
    "CONFLICT_RESOLVED",
    "OTHER",
];
/** Why a candidate waits for a human (`memoryReviewReasons` of the domain). */
export const memoryReviewReasonValues = [
    "INFERRED",
    "PERSONAL_NEEDS_REMEMBER",
    "KIND_REQUIRES_REVIEW",
    "FREE_TEXT",
    "LOW_CONFIDENCE",
    "RECENT_CUSTOMER_DELETION",
    "UPDATE_PROPOSED",
    "CONFLICT",
    "CONFLICTS_WITH_CANDIDATE",
    "SCOPE_AMBIGUOUS",
];
/**
 * The closed v1 vocabulary: the codes a client may offer for each kind (`MEMORY_VOCABULARY_V1` of the domain).
 * The label of a code is the i18n key `memoryApiLabelKey(kind, code)`; the clients own the wording
 * (`memory-labels.ts`). `TRAVEL_COMPANION_PATTERN` and the allergy, religious-diet and access codes are
 * PERSONAL in the domain: a customer may still add them in the app (their own confirmed act).
 */
export const memoryVocabularyCodesV1 = Object.freeze({
    DIETARY_PREFERENCE: [
        "vegetarian",
        "vegan",
        "pescatarian",
        "gluten_free",
        "dairy_free",
        "no_pork",
        "no_beef",
        "no_alcohol",
        "low_spice",
        "halal",
        "kosher",
        "jain",
        "nut_allergy",
        "peanut_allergy",
        "shellfish_allergy",
        "fish_allergy",
        "egg_allergy",
        "sesame_allergy",
    ],
    DINING_STYLE: [
        "fine_dining",
        "casual",
        "tasting_menu",
        "chefs_table",
        "family_style",
        "street_food",
        "quiet",
        "lively",
        "romantic",
        "outdoor_seating",
        "private_room",
        "counter_seating",
    ],
    PROPERTY_PREFERENCE: [
        "quiet_room",
        "high_floor",
        "low_floor",
        "sea_view",
        "connecting_rooms",
        "twin_beds",
        "king_bed",
        "private_pool",
        "villa",
        "boutique_hotel",
        "large_hotel",
        "non_smoking",
        "accessible_room",
        "step_free_access",
    ],
    SCHEDULE_PREFERENCE: [
        "early_riser",
        "late_riser",
        "early_dinner",
        "late_dinner",
        "no_early_flights",
        "no_overnight_flights",
        "relaxed_pace",
        "full_days",
        "afternoon_rest",
    ],
    INTEREST: [
        "modern_art",
        "classical_art",
        "architecture",
        "history",
        "wine",
        "cocktails",
        "food_markets",
        "live_jazz",
        "classical_music",
        "opera",
        "theatre",
        "fashion",
        "shopping",
        "hiking",
        "beaches",
        "wellness",
        "spa",
        "golf",
        "sailing",
        "wildlife",
        "photography",
        "design",
        "nightlife",
        "cooking_classes",
        "sport_events",
    ],
    DISLIKED_EXPERIENCE: [
        "crowds",
        "long_queues",
        "tourist_spots",
        "early_starts",
        "long_drives",
        "boat_trips",
        "heights",
        "loud_venues",
    ],
    TRAVEL_COMPANION_PATTERN: [
        "solo",
        "partner",
        "family_with_children",
        "adult_family",
        "friends",
        "colleagues",
        "with_pet",
    ],
    EXPLICIT_INSTRUCTION: [
        "window_seat",
        "aisle_seat",
        "confirm_before_booking",
        "private_transfers",
        "no_calls_before_10",
        "prefer_written_updates",
    ],
});
const LABEL_KIND = {
    DIETARY_PREFERENCE: "dietary",
    DINING_STYLE: "dining_style",
    PROPERTY_PREFERENCE: "property",
    SCHEDULE_PREFERENCE: "schedule",
    INTEREST: "interest",
    DISLIKED_EXPERIENCE: "disliked",
    TRAVEL_COMPANION_PATTERN: "companion",
    EXPLICIT_INSTRUCTION: "instruction",
};
/** The i18n key of a vocabulary code, for example `memory.dietary.vegetarian`. */
export const memoryApiLabelKey = (kind, code) => `memory.${LABEL_KIND[kind]}.${code}`;
/** The i18n key of a kind heading, for example `memory.kind.dietary_preference`. */
export const memoryKindLabelKey = (kind) => `memory.kind.${kind.toLowerCase()}`;
// --- shared parts ----------------------------------------------------------------------------------
const id = z.guid();
const instant = z.iso.datetime({ offset: true });
const nullableInstant = instant.nullable();
const version = z.number().int().min(1).max(2_147_483_647);
export const memoryKindSchema = z.enum(memoryKindValues);
export const memoryStatusSchema = z.enum(memoryStatusValues);
export const memoryProvenanceSchema = z.enum(memoryProvenanceValues);
export const memoryFormSchema = z.enum(memoryFormValues);
export const memoryStaffReasonCodeSchema = z.enum(memoryStaffReasonCodeValues);
export const memoryReviewReasonSchema = z.enum(memoryReviewReasonValues);
/** A value a client sends (a code such as `vegetarian`, or a short text): the domain classifies it. */
export const memoryValueInputSchema = z.string().min(1).max(MEMORY_VALUE_LIMITS.textMax);
/** The expected version of a fact the client read. 1 or more: a fact starts at version 1. */
export const memoryExpectedVersionSchema = version;
/**
 * The customer's personalization controls. `version` is 0 while the customer never changed a control
 * (the defaults), else the row version. Staff read the same shape and cannot change it.
 */
export const customerMemoryControlsSchema = z.strictObject({
    paused: z.boolean(),
    disabledKinds: z.array(memoryKindSchema).max(memoryKindValues.length),
    version: z.number().int().min(0).max(2_147_483_647),
});
// --- the customer view -----------------------------------------------------------------------------
/**
 * One fact as its customer sees it. Only ACTIVE, unexpired, customer-visible facts exist here (design
 * ruling R10: a candidate or a disputed fact is staff-only). `value` is the vocabulary code for `CODE`
 * (show `labelKey`) and the customer's own text for `TEXT` (`labelKey` is `null`). `version` is the token
 * the next change must send as `expectedVersion`.
 */
export const customerMemoryFactSchema = z.strictObject({
    id,
    kind: memoryKindSchema,
    form: memoryFormSchema,
    value: z.string().min(1).max(1000),
    labelKey: z.string().min(1).max(96).nullable(),
    origin: z.enum(customerMemoryOriginValues),
    lastConfirmedAt: nullableInstant,
    version,
});
export const customerMemoryGroupSchema = z.strictObject({
    kind: memoryKindSchema,
    labelKey: z.string().min(1).max(96),
    facts: z.array(customerMemoryFactSchema).max(200),
});
/** `GET /customer/memory`. Groups are in kind order and only kinds with a fact appear. */
export const customerMemoryViewSchema = z.strictObject({
    schemaVersion: z.literal(CUSTOMER_MEMORY_VIEW_VERSION),
    controls: customerMemoryControlsSchema,
    groups: z.array(customerMemoryGroupSchema).max(memoryKindValues.length),
});
// --- customer commands -----------------------------------------------------------------------------
/** M-C2. A new fact. The customer is the verified identity: the body has no customer id, status or source. */
export const customerMemoryRecordBodySchema = z.strictObject({
    kind: memoryKindSchema,
    value: memoryValueInputSchema,
});
/**
 * M-C3. A correction makes a NEW fact (new id) and supersedes the old one. `kind` must equal the fact's own
 * kind (a correction never moves a fact to another kind), else 422.
 */
export const customerMemoryCorrectBodySchema = z.strictObject({
    expectedVersion: memoryExpectedVersionSchema,
    kind: memoryKindSchema,
    value: memoryValueInputSchema,
});
/** M-C4 (confirm) and M-C5 (delete). */
export const customerMemoryVersionBodySchema = z.strictObject({
    expectedVersion: memoryExpectedVersionSchema,
});
/**
 * M-C6. Replaces the controls. `expectedVersion` is the `controls.version` the customer read: 0 (or `null`)
 * when they never changed a control. A stale version is 409.
 */
export const customerMemoryControlsBodySchema = z.strictObject({
    expectedVersion: z.number().int().min(0).max(2_147_483_647).nullable(),
    paused: z.boolean(),
    disabledKinds: z.array(memoryKindSchema).max(memoryKindValues.length),
});
export const customerMemoryCommandNames = [
    "RECORD",
    "CORRECT",
    "CONFIRM",
    "DELETE",
    "UPDATE_CONTROLS",
];
/**
 * What a customer command answers: ids and a version, never a value. `factId` is the NEW fact for
 * RECORD and CORRECT, the same fact for CONFIRM and DELETE, and `null` for UPDATE_CONTROLS. `version` is
 * the new version of that fact (or of the controls). The client re-reads `GET /customer/memory`.
 */
export const customerMemoryCommandOutcomeSchema = z.strictObject({
    command: z.enum(customerMemoryCommandNames),
    factId: id.nullable(),
    supersededFactId: id.nullable(),
    version,
});
// --- errors ----------------------------------------------------------------------------------------
/**
 * The extra key of a memory 409 answer. The envelope `error.code` is `conflict`; the key `memoryConflict`
 * (a sibling of `error`) says which rule refused the change. `currentVersion` is the version of the fact
 * (or controls) now, so a client can show "This changed on another device" and re-read. It is `null` when
 * the fact is no longer in the state the caller may address (superseded, deleted, expired). `currentStatus`
 * is `null` on every customer answer and the fact status on a staff answer. No value is ever in the body.
 */
export const memoryConflictCodes = [
    "memory.version_conflict",
    "memory.transition_invalid",
    "memory.duplicate_open_fact",
    "memory.tombstoned",
];
export const memoryConflictSchema = z.strictObject({
    code: z.enum(memoryConflictCodes),
    currentVersion: version.nullable(),
    currentStatus: memoryStatusSchema.nullable(),
});
/**
 * The extra key of a memory 422 answer (the request is well formed but the domain refuses it): the NAME of
 * the field and nothing else. Never the value, the rule text or a list of allowed codes.
 */
export const memoryInvalidFieldValues = [
    "kind",
    "value",
    "expectedVersion",
    "reasonCode",
    "sourceType",
    "sourceReference",
    "requestedStatus",
    "disabledKinds",
    "paused",
    "other",
];
export const memoryInvalidSchema = z.strictObject({ field: z.enum(memoryInvalidFieldValues) });
const CUSTOMER_COMMON_ERRORS = [
    "401 unauthenticated",
    "403 forbidden",
    "429 rate_limited",
];
/** The customer memory route table (design `phase-5-memory-api.md`, section 2). `/customer/*` is rate class `customer`. */
export const CUSTOMER_MEMORY_ROUTES = Object.freeze({
    view: {
        id: "M-C1",
        method: "GET",
        path: "/customer/memory",
        authorization: "customer",
        request: null,
        response: "customerMemoryViewSchema",
        success: 200,
        idempotent: false,
        errors: [...CUSTOMER_COMMON_ERRORS],
    },
    record: {
        id: "M-C2",
        method: "POST",
        path: "/customer/memory/facts",
        authorization: "customer",
        request: "customerMemoryRecordBodySchema",
        response: "customerMemoryCommandOutcomeSchema",
        success: 201,
        idempotent: true,
        errors: [
            ...CUSTOMER_COMMON_ERRORS,
            "400 invalid_request",
            "409 conflict (memory.duplicate_open_fact)",
            "413 payload_too_large",
            "415 invalid_request",
            "422 unprocessable (memoryInvalid)",
        ],
    },
    correct: {
        id: "M-C3",
        method: "POST",
        path: "/customer/memory/facts/:factId/correct",
        authorization: "customer",
        request: "customerMemoryCorrectBodySchema",
        response: "customerMemoryCommandOutcomeSchema",
        success: 200,
        idempotent: true,
        errors: [
            ...CUSTOMER_COMMON_ERRORS,
            "400 invalid_request",
            "404 not_found (another customer's fact is the same)",
            "409 conflict (memory.version_conflict, memory.transition_invalid, memory.duplicate_open_fact, memory.tombstoned)",
            "413 payload_too_large",
            "415 invalid_request",
            "422 unprocessable (memoryInvalid)",
        ],
    },
    confirm: {
        id: "M-C4",
        method: "POST",
        path: "/customer/memory/facts/:factId/confirm",
        authorization: "customer",
        request: "customerMemoryVersionBodySchema",
        response: "customerMemoryCommandOutcomeSchema",
        success: 200,
        idempotent: true,
        errors: [
            ...CUSTOMER_COMMON_ERRORS,
            "400 invalid_request",
            "404 not_found",
            "409 conflict (memory.version_conflict, memory.transition_invalid, memory.tombstoned)",
            "413 payload_too_large",
            "415 invalid_request",
        ],
    },
    delete: {
        id: "M-C5",
        method: "POST",
        path: "/customer/memory/facts/:factId/delete",
        authorization: "customer",
        request: "customerMemoryVersionBodySchema",
        response: "customerMemoryCommandOutcomeSchema",
        success: 200,
        idempotent: true,
        errors: [
            ...CUSTOMER_COMMON_ERRORS,
            "400 invalid_request",
            "404 not_found",
            "409 conflict (memory.version_conflict, memory.transition_invalid, memory.tombstoned)",
            "413 payload_too_large",
            "415 invalid_request",
        ],
    },
    controls: {
        id: "M-C6",
        method: "PUT",
        path: "/customer/memory/controls",
        authorization: "customer",
        request: "customerMemoryControlsBodySchema",
        response: "customerMemoryCommandOutcomeSchema",
        success: 200,
        idempotent: true,
        errors: [
            ...CUSTOMER_COMMON_ERRORS,
            "400 invalid_request",
            "409 conflict (memory.version_conflict)",
            "413 payload_too_large",
            "415 invalid_request",
            "422 unprocessable (memoryInvalid)",
        ],
    },
});
export const CUSTOMER_MEMORY_PATHS = Object.freeze({
    view: CUSTOMER_MEMORY_ROUTES.view.path,
    record: CUSTOMER_MEMORY_ROUTES.record.path,
    controls: CUSTOMER_MEMORY_ROUTES.controls.path,
});
export const customerMemoryFactSubpaths = ["correct", "confirm", "delete"];
/** The path of a command on one fact. The id is encoded, so a hostile id cannot add a segment. */
export function customerMemoryFactPath(factId, subpath) {
    return `/customer/memory/facts/${encodeURIComponent(factId)}/${subpath}`;
}
/** The refusal body of a memory 409, as the typed client reads it. */
export const memoryConflictEnvelopeSchema = z.object({
    error: z.object({ code: z.literal("conflict"), message: z.string() }),
    requestId: z.string().min(1).max(128),
    correlationId: z.string().min(1).max(128),
    memoryConflict: memoryConflictSchema,
});
